import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { AppBadge, AppSelect } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import {
  IconCheck,
  IconHourglass,
  IconWarning,
} from "../../../shared/components/icons";
import { listInstallations } from "../api";
import {
  customerDisplayName,
  useCustomersMap,
  useDeviceSerialMap,
  useUserNameMap,
} from "../enrichment";
import type { InstallationStatus } from "../types";
import { KeyApprovalProcessStrip } from "../components/KeyApprovalProcessStrip";

type StatusFilter = InstallationStatus | "";

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "pending_approval", label: "Pending approval" },
  { value: "approved", label: "Approved" },
  { value: "correction_requested", label: "Correction requested" },
  { value: "", label: "All statuses" },
];

interface QueueRow {
  id: number;
  customer_id: number;
  customerName: string;
  ward: string;
  technician: string;
  serial: string;
  created_at: string;
  status: InstallationStatus;
}

function statusBadge(status: InstallationStatus) {
  if (status === "pending_approval")
    return (
      <AppBadge tone="warning">
        <IconWarning size={12} /> Pending
      </AppBadge>
    );
  if (status === "approved")
    return (
      <AppBadge tone="success">
        <IconCheck size={12} /> Approved
      </AppBadge>
    );
  if (status === "correction_requested")
    return (
      <AppBadge tone="info">
        <IconHourglass size={12} /> Correction requested
      </AppBadge>
    );
  return <AppBadge tone="neutral">{status}</AppBadge>;
}

function formatSubmitted(iso: string): string {
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

export function S6KeyApprovalQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>("pending_approval");

  const query = useQuery({
    queryKey: ["inst-key-queue", status],
    queryFn: () =>
      listInstallations({
        status: status || undefined,
        perPage: 100,
      }),
    retry: 1,
    staleTime: 15_000,
  });

  const installations = useMemo(() => query.data?.installations ?? [], [query.data]);

  const customers = useCustomersMap(installations.map((i) => i.customer_id));
  const technicians = useUserNameMap();
  const serials = useDeviceSerialMap();

  const rows: QueueRow[] = useMemo(
    () =>
      installations.map((i) => {
        const c = customers.get(i.customer_id);
        return {
          id: i.id,
          customer_id: i.customer_id,
          customerName: customerDisplayName(c, i.customer_id),
          ward: c?.ward ?? "—",
          technician:
            i.technician_id != null
              ? (technicians.get(i.technician_id) ?? `#${i.technician_id}`)
              : "—",
          serial: i.device_id != null ? (serials.get(i.device_id) ?? "—") : "—",
          created_at: i.created_at,
          status: i.status,
        };
      }),
    [installations, customers, technicians, serials],
  );

  const columns = useMemo<ColumnDef<QueueRow, unknown>[]>(
    () => [
      { accessorKey: "customerName", header: "Customer" },
      {
        accessorKey: "status",
        header: "Status",
        cell: (c) => statusBadge(c.getValue() as InstallationStatus),
      },
      { accessorKey: "technician", header: "Technician", meta: { hideBelow: "md" } },
      { accessorKey: "ward", header: "Ward", meta: { hideBelow: "md" } },
      {
        accessorKey: "serial",
        header: "Device serial",
        enableSorting: false,
        meta: { hideBelow: "lg" },
      },
      {
        accessorKey: "created_at",
        header: "Submitted",
        cell: (c) => formatSubmitted(c.getValue() as string),
        meta: { hideBelow: "lg" },
      },
      {
        accessorKey: "id",
        header: "Installation ID",
        meta: { technical: true },
      },
    ],
    [],
  );

  const title =
    status === "pending_approval" && query.data
      ? `Key Approvals (${query.data.total} pending)`
      : "Key Approvals";

  if (query.isPending) {
    return (
      <>
        <PageHeader
          title="Key Approvals"
          subtitle="Office approval of submitted installations — issues the first OpenPAYGO unlock (not chairman, not cash payment approval)."
        />
        <PageState status="loading" />
      </>
    );
  }

  if (query.isError) {
    return (
      <>
        <PageHeader
          title="Key Approvals"
          subtitle="Office approval of submitted installations — issues the first OpenPAYGO unlock (not chairman, not cash payment approval)."
        />
        <PageState
          status="error"
          error={query.error instanceof Error ? query.error.message : "Something went wrong"}
          onRetry={() => void query.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle="Office approval of submitted installations — issues the first OpenPAYGO unlock (not chairman, not cash payment approval)."
        actions={
          <AppSelect
            id="inst-status-filter"
            aria-label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </AppSelect>
        }
      />

      <KeyApprovalProcessStrip activeStep={0} />

      {rows.length === 0 ? (
        <PageState
          status="empty"
          emptyTitle="Queue clear"
          emptyHint={
            status
              ? `No ${STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()} installations.`
              : "No installations."
          }
        />
      ) : (
        <AppDataTable
          data={rows}
          columns={columns}
          onRowClick={(row) => navigate(`/key-approvals/${row.id}`)}
        />
      )}
    </>
  );
}
